import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useBrand } from "@/context/BrandContext";
import LoginPage from "@/pages/LoginPage";

/**
 * Public bare-slug landing — resolves `/[slug]` (any pub slug) to
 * the branded login / register experience. Mounted before the catch-all
 * so unknown slugs still surface a usable LoginPage instead of a 404.
 *
 * Flow:
 *   1. Try to load the brand for the slug (via BrandContext →
 *      GET /api/pubs/by-slug/[slug]).
 *   2. If found → apply brand context + render <LoginPage/>.
 *   3. If NOT found (legacy pubs that never got a `pubs.*` row, typos,
 *      dead links…) → still render <LoginPage/> using the default Dugsy
 *      brand. Previously this redirected to `/` which dropped users on
 *      the platform-wide home — the wrong UX and a source of complaints
 *      ("clicking my pub link takes me to Dugsy home instead of my login").
 *      Now the URL stays put so the user can sign in from the same link.
 */
export default function SluggedLoginPage() {
  const { slug } = useParams();
  const { setBrandBySlug } = useBrand();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await setBrandBySlug(slug);
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [slug, setBrandBySlug]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Loading pub…</div>
      </div>
    );
  }
  return <LoginPage />;
}
